import { useCallback, useMemo, useRef, useState } from 'react';
import { apiPath } from './constants';
import { isOidcConfigured, userManager } from './oidc';
import type { ReasonExportErrorBody, ReasonWorkResultsExportPlan } from './seccapReason/types';

export type { ReasonExportErrorBody, ReasonWorkResultsExportPlan } from './seccapReason/types';

export type CsvExportPhase =
  | 'planning'
  | 'preparing'
  | 'downloading'
  | 'saving'
  | 'complete'
  | 'failed';

export type CsvExportState = {
  label: string;
  phase: CsvExportPhase;
  progress: number | null;
  detail?: string;
  part?: number;
  totalParts?: number;
  completedParts?: number;
};

export type CsvDownloadInput = {
  path: string;
  filename: string;
  label: string;
};

export type CsvExportPlanInput = {
  path: string;
  label: string;
  query?: Record<string, string>;
};

export type CsvPlannedDownloadInput = {
  plan: ReasonWorkResultsExportPlan;
  csvPath: string;
  label: string;
  completedParts?: number[];
};

export class CsvExportError extends Error {
  readonly status: number | null;
  readonly code: string | null;
  readonly detail: string;
  readonly segmentCount: number | null;
  readonly maxSegments: number | null;

  constructor(
    detail: string,
    status: number | null = null,
    code: string | null = null,
    body: ReasonExportErrorBody | null = null
  ) {
    super(detail);
    this.name = 'CsvExportError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.segmentCount = body?.segment_count ?? null;
    this.maxSegments = body?.max_segments ?? null;
  }
}

export function csvExportPhaseLabel(phase: CsvExportPhase): string {
  switch (phase) {
    case 'planning':
      return 'Planning export';
    case 'preparing':
      return 'Preparing export';
    case 'downloading':
      return 'Downloading CSV';
    case 'saving':
      return 'Saving file';
    case 'complete':
      return 'Export complete';
    case 'failed':
      return 'Export failed';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function buildPath(path: string, query?: Record<string, string>): string {
  if (!query) return path;
  const search = new URLSearchParams(query).toString();
  if (!search) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${search}`;
}

function errorBodyFromText(text: string): ReasonExportErrorBody | null {
  if (!text.trim()) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  const detail =
    typeof parsed.detail === 'string'
      ? parsed.detail
      : typeof parsed.message === 'string'
        ? parsed.message
        : typeof parsed.error === 'string'
          ? parsed.error
          : typeof parsed.reason === 'string'
            ? parsed.reason
            : null;
  if (!detail) return null;
  return {
    code: typeof parsed.code === 'string' ? parsed.code : 'EXPORT_REQUEST_FAILED',
    detail,
    ...(isNonNegativeInteger(parsed.segment_count) ? { segment_count: parsed.segment_count } : {}),
    ...(isNonNegativeInteger(parsed.max_segments) ? { max_segments: parsed.max_segments } : {}),
  };
}

export async function csvExportErrorFromResponse(response: Response): Promise<CsvExportError> {
  let text = '';
  try {
    text = await response.text();
  } catch {
    // The status is still useful when the response body cannot be read.
  }
  const body = errorBodyFromText(text);
  const detail = body?.detail ?? (text.trim() || `HTTP ${response.status}`);
  return new CsvExportError(detail, response.status, body?.code ?? null, body);
}

function toCsvExportError(error: unknown, fallback: string): CsvExportError {
  if (error instanceof CsvExportError) return error;
  const message = error instanceof Error ? error.message.trim() : '';
  return new CsvExportError(message || fallback, null, 'EXPORT_FAILED');
}

function visibleErrorDetail(error: CsvExportError): string {
  return error.code ? `${error.detail} (${error.code})` : error.detail;
}

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  if (isOidcConfigured) {
    const user = await userManager.getUser();
    if (user?.access_token) headers.Authorization = `Bearer ${user.access_token}`;
  }
  return headers;
}

async function fetchCsvResponse(path: string, headers: Record<string, string>): Promise<Response> {
  const response = await fetch(apiPath(path), {
    headers,
    credentials: 'same-origin',
  });
  if (!response.ok) throw await csvExportErrorFromResponse(response);
  return response;
}

function parseExportPlan(value: unknown): ReasonWorkResultsExportPlan {
  const raw = isRecord(value) && isRecord(value.plan) ? value.plan : value;
  if (!isRecord(raw)) {
    throw new CsvExportError('The export plan response was invalid.', null, 'INVALID_EXPORT_PLAN');
  }

  const readInteger = (field: string): number => {
    const fieldValue = raw[field];
    if (!isNonNegativeInteger(fieldValue)) {
      throw new CsvExportError(
        'The export plan response was invalid.',
        null,
        'INVALID_EXPORT_PLAN'
      );
    }
    return fieldValue;
  };
  if (raw.from_ms !== null && !isNonNegativeInteger(raw.from_ms)) {
    throw new CsvExportError('The export plan response was invalid.', null, 'INVALID_EXPORT_PLAN');
  }
  if (!Array.isArray(raw.parts)) {
    throw new CsvExportError('The export plan response was invalid.', null, 'INVALID_EXPORT_PLAN');
  }
  if (typeof raw.environment !== 'string' || !raw.environment) {
    throw new CsvExportError('The export plan response was invalid.', null, 'INVALID_EXPORT_PLAN');
  }

  const parts = raw.parts.map((value) => {
    if (!isRecord(value)) {
      throw new CsvExportError(
        'The export plan response was invalid.',
        null,
        'INVALID_EXPORT_PLAN'
      );
    }
    if (
      !isNonNegativeInteger(value.part) ||
      value.part < 1 ||
      !isNonNegativeInteger(value.segment_count) ||
      typeof value.filename !== 'string' ||
      !value.filename ||
      typeof value.token !== 'string' ||
      !value.token
    ) {
      throw new CsvExportError(
        'The export plan response was invalid.',
        null,
        'INVALID_EXPORT_PLAN'
      );
    }
    return {
      part: value.part,
      segment_count: value.segment_count,
      filename: value.filename,
      token: value.token,
    };
  });

  return {
    environment: raw.environment,
    generated_at_ms: readInteger('generated_at_ms'),
    expires_at_ms: readInteger('expires_at_ms'),
    snapshot_to_ms: readInteger('snapshot_to_ms'),
    from_ms: raw.from_ms,
    to_ms: readInteger('to_ms'),
    episode_count: readInteger('episode_count'),
    segment_count: readInteger('segment_count'),
    selection_count: readInteger('selection_count'),
    max_segments_per_file: readInteger('max_segments_per_file'),
    parts,
  };
}

type CsvBlobProgress = (receivedBytes: number, contentLength: number) => void;

async function readCsvBlob(response: Response, onProgress?: CsvBlobProgress): Promise<Blob> {
  const contentLengthHeader = Number(response.headers.get('content-length') ?? '');
  const contentLength = Number.isFinite(contentLengthHeader) ? contentLengthHeader : 0;
  if (!response.body) return response.blob();

  const reader = response.body.getReader();
  const chunks: ArrayBuffer[] = [];
  let received = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    const copy = new Uint8Array(value.byteLength);
    copy.set(value);
    chunks.push(copy.buffer);
    received += value.byteLength;
    onProgress?.(received, contentLength);
  }
  return new Blob(chunks, {
    type: response.headers.get('content-type') ?? 'text/csv;charset=utf-8',
  });
}

function saveCsvBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    URL.revokeObjectURL(url);
  }
}

function progressForSegments(completedSegments: number, totalSegments: number): number | null {
  if (totalSegments <= 0) return null;
  return Math.min(99, Math.max(0, Math.round((completedSegments / totalSegments) * 100)));
}

export function useCsvExport() {
  const [exportState, setExportState] = useState<CsvExportState | null>(null);
  const [exportPlan, setExportPlan] = useState<ReasonWorkResultsExportPlan | null>(null);
  const [completedParts, setCompletedParts] = useState<number[]>([]);
  const [failedPart, setFailedPart] = useState<number | null>(null);
  const activeExportRef = useRef(false);
  const isExporting = useMemo(
    () => exportState != null && exportState.phase !== 'complete' && exportState.phase !== 'failed',
    [exportState]
  );

  const clearExportState = useCallback(() => {
    setExportState(null);
    setExportPlan(null);
    setCompletedParts([]);
    setFailedPart(null);
  }, []);

  const downloadCsv = useCallback(async ({ path, filename, label }: CsvDownloadInput) => {
    if (activeExportRef.current) return;
    activeExportRef.current = true;
    setExportPlan(null);
    setCompletedParts([]);
    setFailedPart(null);
    setExportState({ label, phase: 'preparing', progress: 8 });
    try {
      const headers = await authHeaders();
      const response = await fetchCsvResponse(path, headers);
      setExportState({ label, phase: 'downloading', progress: 18 });
      let blob: Blob;
      try {
        blob = await readCsvBlob(response, (received, contentLength) => {
          const progress =
            contentLength > 0
              ? Math.min(92, 18 + Math.round((received / contentLength) * 74))
              : null;
          setExportState({ label, phase: 'downloading', progress });
        });
      } catch (error) {
        throw toCsvExportError(
          error,
          'The CSV download was interrupted. Check your connection and retry.'
        );
      }
      setExportState({ label, phase: 'saving', progress: 96 });
      saveCsvBlob(blob, filename);
      setExportState({ label, phase: 'complete', progress: 100 });
    } catch (error) {
      const normalized = toCsvExportError(
        error,
        'The export request failed. Check your connection and retry.'
      );
      setExportState({
        label,
        phase: 'failed',
        progress: null,
        detail: visibleErrorDetail(normalized),
      });
      throw normalized;
    } finally {
      activeExportRef.current = false;
    }
  }, []);

  const planCsvExport = useCallback(async ({ path, label, query }: CsvExportPlanInput) => {
    if (activeExportRef.current) {
      throw new CsvExportError('An export is already in progress.', null, 'EXPORT_IN_PROGRESS');
    }
    activeExportRef.current = true;
    setExportPlan(null);
    setCompletedParts([]);
    setFailedPart(null);
    setExportState({ label, phase: 'planning', progress: 4 });
    try {
      const headers = await authHeaders();
      const response = await fetchCsvResponse(buildPath(path, query), headers);
      let payload: unknown;
      try {
        payload = await response.json();
      } catch {
        throw new CsvExportError(
          'The export plan response was not valid JSON.',
          response.status,
          'INVALID_EXPORT_PLAN'
        );
      }
      const plan = parseExportPlan(payload);
      setExportPlan(plan);
      setExportState({
        label,
        phase: 'preparing',
        progress: plan.segment_count > 0 ? 0 : null,
        totalParts: plan.parts.length,
        completedParts: 0,
      });
      return plan;
    } catch (error) {
      const normalized = toCsvExportError(
        error,
        'The export plan request failed. Check your connection and retry.'
      );
      setExportState({
        label,
        phase: 'failed',
        progress: null,
        detail: visibleErrorDetail(normalized),
      });
      throw normalized;
    } finally {
      activeExportRef.current = false;
    }
  }, []);

  const downloadPlannedCsv = useCallback(
    async ({
      plan,
      csvPath,
      label,
      completedParts: requestedCompletedParts = [],
    }: CsvPlannedDownloadInput) => {
      if (activeExportRef.current) return;
      activeExportRef.current = true;
      const orderedParts = [...plan.parts].sort((left, right) => left.part - right.part);
      const completedPartSet = new Set(
        requestedCompletedParts.filter((part) =>
          orderedParts.some((candidate) => candidate.part === part)
        )
      );
      const initialCompletedParts = orderedParts
        .filter((part) => completedPartSet.has(part.part))
        .map((part) => part.part);
      let completedSegments = orderedParts
        .filter((part) => completedPartSet.has(part.part))
        .reduce((total, part) => total + part.segment_count, 0);
      let currentPart: number | null = null;
      setCompletedParts(initialCompletedParts);
      setFailedPart(null);
      setExportState({
        label,
        phase: 'preparing',
        progress: progressForSegments(completedSegments, plan.segment_count),
        totalParts: orderedParts.length,
        completedParts: initialCompletedParts.length,
      });

      try {
        const headers = await authHeaders();
        const remainingParts = orderedParts.filter((part) => !completedPartSet.has(part.part));
        for (const part of remainingParts) {
          currentPart = part.part;
          setExportState({
            label,
            phase: 'downloading',
            progress: progressForSegments(completedSegments, plan.segment_count),
            part: part.part,
            totalParts: orderedParts.length,
            completedParts: completedPartSet.size,
          });
          const response = await fetchCsvResponse(
            buildPath(csvPath, { part_token: part.token }),
            headers
          );
          const blob = await readCsvBlob(response, (received, contentLength) => {
            const partFraction = contentLength > 0 ? Math.min(1, received / contentLength) : null;
            const estimatedSegments =
              partFraction == null
                ? completedSegments
                : completedSegments + part.segment_count * partFraction;
            const progress =
              plan.segment_count > 0
                ? Math.min(99, Math.round((estimatedSegments / plan.segment_count) * 100))
                : null;
            setExportState({
              label,
              phase: 'downloading',
              progress,
              part: part.part,
              totalParts: orderedParts.length,
              completedParts: completedPartSet.size,
            });
          });
          setExportState({
            label,
            phase: 'saving',
            progress: progressForSegments(completedSegments, plan.segment_count),
            part: part.part,
            totalParts: orderedParts.length,
            completedParts: completedPartSet.size,
          });
          saveCsvBlob(blob, part.filename);
          completedPartSet.add(part.part);
          completedSegments += part.segment_count;
          const nextCompletedParts = orderedParts
            .filter((candidate) => completedPartSet.has(candidate.part))
            .map((candidate) => candidate.part);
          setCompletedParts(nextCompletedParts);
          setExportState({
            label,
            phase: 'downloading',
            progress: progressForSegments(completedSegments, plan.segment_count),
            part: part.part,
            totalParts: orderedParts.length,
            completedParts: nextCompletedParts.length,
          });
        }
        setExportState({
          label,
          phase: 'saving',
          progress: plan.segment_count > 0 ? 99 : null,
          totalParts: orderedParts.length,
          completedParts: completedPartSet.size,
        });
        setExportState({
          label,
          phase: 'complete',
          progress: 100,
          totalParts: orderedParts.length,
          completedParts: completedPartSet.size,
        });
        setFailedPart(null);
      } catch (error) {
        const normalized = toCsvExportError(
          error,
          currentPart == null
            ? 'The export download failed. Check your connection and retry.'
            : `Part ${currentPart} failed while downloading. Retry this part.`
        );
        setFailedPart(currentPart);
        setExportState({
          label,
          phase: 'failed',
          progress: null,
          detail: visibleErrorDetail(normalized),
          ...(currentPart == null ? {} : { part: currentPart }),
          totalParts: orderedParts.length,
          completedParts: completedPartSet.size,
        });
        throw normalized;
      } finally {
        activeExportRef.current = false;
      }
    },
    []
  );

  return {
    exportState,
    exportPlan,
    completedParts,
    failedPart,
    isExporting,
    downloadCsv,
    planCsvExport,
    downloadPlannedCsv,
    clearExportState,
  };
}
